import ClearIcon from "@mui/icons-material/Clear";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import {
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	InputAdornment,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "../../../../shared/hooks";
import {
	globalFormatCurrencyBRL,
	globalMaskDocument,
	globalTranslateStatusComission,
	globalTranslateTypeComission,
	translateProposal,
} from "../../../../shared/utils";
import { globalcolorBackGroundComission, globalcolorTextComision } from "../../../../shared/utils/global-utils-comission";
import { useComissionHandlers } from "../../hooks/useComissionHandlers";
import { useComissions } from "../../hooks/useComissions";
import { useInstallmentModalReducer } from "../../hooks/useInstallmentModalReducer";
import { getModalTexts, ModalType } from "../../types/modalTexts";
import { ModalConfirmSelect } from "./ModalConfirmSelect";

interface Props {
	open: boolean;
	onClose: () => void;
	dataIni: string;
	dataFim: string;
	row: { clientId: string; name: string };
	modalType: ModalType;
	filterByPaymentMethod?: "credit_card" | "billet";
	onSuccess?: () => void;
}

export const ModalSelectComission = ({ open, onClose, dataIni, dataFim, row, modalType, filterByPaymentMethod, onSuccess }: Props) => {
	const { rows, isLoading } = useComissions(open, row.clientId, dataIni, dataFim);
	const { state, dispatch } = useInstallmentModalReducer();
	const { selectedIds, openConfirmModal } = state;
	const [searchText, setSearchText] = useState("");
	const [debouncedSearchText, setDebouncedSearchText] = useState("");
	const [obs, setObs] = useState("");
	const [loadingAction, setLoadingAction] = useState(false);
	const { debounce } = useDebounce();
	const [confirmDate, setConfirmDate] = useState("");

	const { handleCancelComissions, handlePaymentComissions, handleCloseCycleComissions } = useComissionHandlers(dataIni, dataFim, setLoadingAction);

	useEffect(() => {
		debounce(() => setDebouncedSearchText(searchText));
	}, [searchText]);

	const normalize = (text: string) => text.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

	const sortedRows = useMemo(() => {
		return [...rows].sort((a, b) => a.proposalName?.localeCompare(b.proposalName ?? "") ?? 0);
	}, [rows]);

	const filteredRows = sortedRows.filter((item) => normalize(`${item.proposalName ?? ""} ${item.proposalDocument}`).includes(normalize(debouncedSearchText)));

	// closeCycle só faz sentido pra quem ainda não foi apurado ("preview") — "open"/"paid" já
	// passaram pelo fechamento ou pagamento, não tem o que fechar de novo.
	const isRowSelectable = (status: string) => (modalType === "closeCycle" ? status === "preview" : status === "preview" || status === "open");
	const allIds = filteredRows.filter((r) => isRowSelectable(r.status)).map((r) => r.id);

	const isAllSelected = selectedIds.length === allIds.length && allIds.length > 0;
	const isIndeterminate = selectedIds.length > 0 && selectedIds.length < allIds.length;

	const toggleSelectAll = () => dispatch({ type: "TOGGLE_SELECT_ALL", payload: allIds });
	const toggleSelectOne = (id: string) => dispatch({ type: "TOGGLE_SELECT_ONE", payload: id });

	const { title, confirmTitle, confirmDescription, confirmLabel, cancelLabel } = getModalTexts(modalType, row.name);

	const totalSelectedValue = useMemo(() => {
		return rows.filter((item) => selectedIds.includes(item.id)).reduce((sum, item) => {
			const value = Number(item.value) || 0;
			return item.type === "discount" ? sum - value : sum + value;
		}, 0);
	}, [rows, selectedIds]);

	const renderCustomContent = () => {
		if (modalType === "cancel" || modalType === "manual") {
			return (
				<Grid container item direction="column" spacing={2}>
					<Grid item xs={12} sm={12} md={4}>
						<TextField
							label={modalType === "cancel" ? "Motivo do cancelamento" : "Motivo da baixa manual"}
							value={obs}
							onChange={(e) => setObs(e.target.value)}
							fullWidth
							multiline
							rows={3}
						/>
					</Grid>
					<Grid item xs={12} sm={12} md={4}>
						<TextField label="Data de confirmação" value={confirmDate} onChange={(e) => setConfirmDate(e.target.value)} fullWidth type="date" />
					</Grid>
				</Grid>
			);
		}

		if (modalType === "convert") {
			const newStatusLabel = filterByPaymentMethod === "credit_card" ? "boleto bancário" : "cartão de crédito";
			return (
				<Typography variant="body2">
					As comissões selecionadas serão convertidas para <strong>{newStatusLabel}</strong>.
				</Typography>
			);
		}

		return null;
	};

	const handleConfirmAction = async () => {
		let success = false;

		if (modalType === "cancel") {
			await handleCancelComissions(obs, confirmDate.substring(0, 10) + " 03:00:00", selectedIds);
			success = true;
		} else if (modalType === "manual") {
			await handlePaymentComissions(obs, confirmDate.substring(0, 10) + " 03:00:00", selectedIds);
			success = true;
		} else if (modalType === "closeCycle") {
			await handleCloseCycleComissions(selectedIds);
			success = true;
		}

		dispatch({ type: "CLOSE_CONFIRM_MODAL" });
		if (success && onSuccess) {
			setTimeout(() => {
				onSuccess();
			}, 200);
		}
		onClose();
	};

	return (
		<>
			<Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
				<DialogTitle
					sx={{
						color: "primary.main",
						fontWeight: 400,
						display: "flex",
						justifyContent: "space-between",
					}}
				>
					{title}
					<IconButton onClick={onClose} size="small">
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					{isLoading ? (
						<Stack alignItems="center" py={4}>
							<CircularProgress />
							<Typography variant="body2" mt={2}>
								Carregando...
							</Typography>
						</Stack>
					) : (
						<>
							<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
								<TextField
									placeholder="Pesquisar por nome ou documento"
									variant="outlined"
									size="small"
									fullWidth
									value={searchText}
									onChange={(e) => setSearchText(e.target.value)}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon />
											</InputAdornment>
										),
										endAdornment: searchText && (
											<InputAdornment position="end">
												<IconButton size="small" onClick={() => setSearchText("")}>
													<ClearIcon fontSize="small" />
												</IconButton>
											</InputAdornment>
										),
									}}
									sx={{ maxWidth: 400 }}
								/>
								<Box sx={{ display: "flex", alignItems: "right", flexDirection: "column", justifyContent: "space-between" }}>
									<Typography variant="body2" sx={{ ml: 2 }}>
										{filteredRows.length} registro(s) encontrado(s)
									</Typography>
									{selectedIds.length > 0 && (
										<Typography variant="body2" sx={{ ml: 1, color: "primary.main", fontWeight: "bold" }}>
											{`Selecionado(s): ${selectedIds.length} — Total: ${globalFormatCurrencyBRL(totalSelectedValue.toString())}`}
										</Typography>
									)}
								</Box>
							</Stack>

							<Box sx={{ maxHeight: 420, overflowY: "auto" }}>
								<Table size="small">
									<TableHead>
										<TableRow sx={{ backgroundColor: "grey.100" }}>
											<TableCell padding="checkbox">
												<Checkbox checked={isAllSelected} indeterminate={isIndeterminate} onChange={toggleSelectAll} />
											</TableCell>
											{["CERTIFICADO", "NOME", "TIPO", "VALOR", "VENCIMENTO", "OBSERVAÇÃO", "STATUS"].map((col, i) => (
												<TableCell key={i}>
													<Typography variant="button">{col}</Typography>
												</TableCell>
											))}
										</TableRow>
									</TableHead>

									<TableBody>
										{filteredRows.map((item) => {
											const isSelectable = isRowSelectable(item.status);
											return (
												<TableRow key={item.id} selected={selectedIds.includes(item.id)} hover>
													<TableCell padding="checkbox">
														<Tooltip
															title={!isSelectable ? "Só é possível selecionar comissões pendentes." : ""}
															arrow
															disableHoverListener={isSelectable}
														>
															<span>
																<Checkbox
																	checked={selectedIds.includes(item.id)}
																	onChange={() => toggleSelectOne(item.id)}
																	disabled={!isSelectable}
																/>
															</span>
														</Tooltip>
													</TableCell>
													<TableCell>{item.proposalCertificateNumber ?? "-"}</TableCell>
													<TableCell>
														<Typography fontSize="0.875rem">{item.proposalName ?? "-"}</Typography>
														<Typography fontSize="0.875rem" fontWeight="bold">
															{globalMaskDocument(item.clientDocument)}
														</Typography>
													</TableCell>
													<TableCell>
														<Typography fontSize="0.875rem">{globalTranslateTypeComission(item.type)}</Typography>
														{item.type === "parcel" && item.installmentNumber && item.proposalRentMonthsCount && (
															<Typography variant="caption" color="textSecondary">
																{item.installmentNumber}/{item.proposalRentMonthsCount}
															</Typography>
														)}
													</TableCell>
													<TableCell>
														<Typography fontWeight="bold" color={item.type === "discount" ? "error" : "inherit"}>
															{globalFormatCurrencyBRL(item.value)}
														</Typography>
													</TableCell>
													<TableCell>{format(new Date(item.dueDate), "dd/MM/yyyy")}</TableCell>
													<TableCell>{item.description}</TableCell>
													<TableCell>
														<Chip
															label={translateProposal(globalTranslateStatusComission(item.status))}
															size="small"
															variant="filled"
															sx={{
																backgroundColor: globalcolorBackGroundComission(item.status),
																color: globalcolorTextComision(item.status),
																fontWeight: "normal",
															}}
														/>
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</Box>
						</>
					)}
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>{cancelLabel}</Button>
					<Button variant="contained" color="primary" disabled={selectedIds.length === 0} onClick={() => dispatch({ type: "OPEN_CONFIRM_MODAL" })}>
						{confirmLabel}
					</Button>
				</DialogActions>
			</Dialog>

			<ModalConfirmSelect
				open={openConfirmModal}
				onClose={() => dispatch({ type: "CLOSE_CONFIRM_MODAL" })}
				onConfirm={handleConfirmAction}
				title={confirmTitle}
				description={confirmDescription}
				confirmLabel={confirmLabel}
				cancelLabel={cancelLabel}
				customContent={renderCustomContent()}
				isLoading={loadingAction}
				disabledConfirmButton={["cancel", "manual"].includes(modalType) && obs.trim().length === 0}
			/>
		</>
	);
};
